<template>
  <!-- the title attribute is what makes bootstrap-vue emit aria-labelledby on the dialog -->
  <b-modal
    :id="modalId"
    title="Add entry"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hide="onAddEntryHide"
    @hidden="onAddEntryHidden"
  >
    <div v-if="addEntryLoading" class="od-add-loading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading form...
    </div>
    <div v-else class="od-add-form">
      <div class="od-add-type-group" role="group" aria-label="Entry type">
        <span class="od-add-type-label" aria-hidden="true">Type</span>
        <div class="od-add-type-row">
          <button v-for="t in addEntryOfferedTypeOptions" :key="t.value"
                  type="button"
                  :class="['od-add-type-btn', { 'od-add-type-btn--active': addEntry.entry_type === t.value }]"
                  :aria-pressed="addEntry.entry_type === t.value ? 'true' : 'false'"
                  @click="setAddEntryType(t.value)">
            <i class="fa" :class="t.icon" aria-hidden="true"></i><span>{{ t.label }}</span>
          </button>
        </div>
      </div>

      <div class="od-add-field" v-if="showAddEntryCategory">
        <label for="od-add-category">Category <span class="od-add-req">*</span></label>
        <b-form-select id="od-add-category" v-model="addEntry.category" :options="addEntryCategoryOptions" size="sm"/>
      </div>

      <!-- FI-1820: sos inline on breakdown/accident. opens in a new
           tab so the half-typed entry isn't lost. -->
      <div v-if="showAddEntrySos" class="od-add-sos">
        <span class="od-add-sos-text"><strong>Need roadside help?</strong> You can send an SOS while you log this.</span>
        <router-link :to="addEntrySosLinkTo" target="_blank" class="od-add-sos-btn">
          <i class="fa fa-exclamation-triangle"></i>&nbsp;Open Bus SOS
        </router-link>
      </div>

      <!-- FI-1933 severity: incident-only (kudos / notes are always low),
           pre-set to the category default and re-seeded when the category
           changes; staff can override before submit. -->
      <div class="od-add-field" v-if="addEntry.entry_type === 'incident'">
        <label for="od-add-severity">Severity</label>
        <b-form-select id="od-add-severity" v-model="addEntry.severity" :options="severityEditOptions" size="sm"/>
      </div>

      <!-- category-driven fields, grouped in the same tinted panel
           the public form uses so the block that changes with the
           category reads as one unit. -->
      <div v-if="addEntryConditionalFields.length" class="od-add-conditional">
        <div v-for="f in addEntryConditionalFields" :key="f.key" class="od-add-field">
          <label :for="'od-add-f-' + f.key">{{ f.label }}<span v-if="f.required" class="od-add-req"> *</span></label>
          <div v-if="f.help" class="od-add-help">{{ f.help }}</div>
          <b-form-textarea v-if="f.type === 'textarea'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" :rows="2" max-rows="6" no-resize :placeholder="f.placeholder || ''"/>
          <b-form-select v-else-if="f.type === 'select'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" :options="selectOptions(f)" size="sm"/>
          <b-form-checkbox v-else-if="f.type === 'checkbox'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" size="sm">{{ f.placeholder || 'Yes' }}</b-form-checkbox>
          <b-form-input v-else-if="f.type === 'number'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="number" size="sm" :placeholder="f.placeholder || ''"/>
          <b-form-input v-else-if="f.type === 'tel'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="tel" size="sm" :placeholder="f.placeholder || ''"/>
          <b-form-input v-else-if="f.type === 'email'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="email" size="sm" :placeholder="f.placeholder || ''"/>
          <b-form-input v-else-if="f.type === 'date'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="date" size="sm"/>
          <b-form-input v-else :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" size="sm" :placeholder="f.placeholder || ''"/>
        </div>
        <!-- FI-2152: a driver kudos names its recipient here, right under
             "who is this for?", bound to the same driver_id the context row
             carries (that row's driver column hides in this state so the
             question is asked once). one model, so nothing typed is lost
             when the kind flips; the roster datalist below feeds both. -->
        <div v-if="showKudosDriverField" class="od-add-field">
          <label for="od-add-kudos-driver">Driver being recognized</label>
          <b-form-input id="od-add-kudos-driver" v-model="addEntry.driver_id" size="sm"
                        placeholder="Start typing to pick from the roster"
                        list="od-add-driver-datalist" autocomplete="off"/>
        </div>
      </div>
      <!-- hoisted out of the driver column so it exists for whichever driver
           input is on screen -->
      <datalist id="od-add-driver-datalist">
        <option v-for="d in rosterDrivers" :key="d.ref" :value="d.name"></option>
      </datalist>

      <div class="od-add-field">
        <label for="od-add-what">What happened <span class="od-add-req">*</span></label>
        <b-form-textarea id="od-add-what" v-model="addEntry.what_happened" :rows="3" max-rows="10" no-resize placeholder="Brief description of the event..."/>
      </div>

      <!-- dispatch usually files an event the driver already relayed and
           often knows how it was handled; carrying the note on create
           saves reopening the event just to add it. staff modal only --
           the public form never shows this. -->
      <div class="od-add-field">
        <label for="od-add-follow-up">Follow-up &amp; resolution</label>
        <b-form-textarea id="od-add-follow-up" v-model="addEntry.follow_up_resolution" :rows="2" max-rows="8" no-resize placeholder="What was done about it, if anything yet..."/>
      </div>

      <div class="od-add-row">
        <!-- a driver kudos asks for the driver up in the kudos block instead -->
        <div v-if="!showKudosDriverField" class="od-add-col">
          <label for="od-add-driver">Driver</label>
          <!-- FI-2116 roster typeahead; free text stays possible for a
               name that isn't in the list (it links later on the event) -->
          <b-form-input id="od-add-driver" v-model="addEntry.driver_id" size="sm" placeholder="ID or name"
                        list="od-add-driver-datalist" autocomplete="off"/>
        </div>
        <div class="od-add-col">
          <label for="od-add-vehicle">Vehicle</label>
          <!-- FI-2098 fleet-roster typeahead; free text stays possible for
               a unit that isn't in the list (it links later on the event) -->
          <b-form-input id="od-add-vehicle" v-model="addEntry.vehicle_id" size="sm" placeholder="ID"
                        list="od-add-vehicle-datalist" autocomplete="off"/>
          <datalist id="od-add-vehicle-datalist">
            <option v-for="v in rosterVehicles" :key="v" :value="v"></option>
          </datalist>
        </div>
        <div class="od-add-col">
          <label for="od-add-charter">Charter ref</label>
          <b-form-input id="od-add-charter" v-model="addEntry.charter_ref" size="sm" placeholder="Optional"/>
        </div>
      </div>

      <!-- FI-2201 (#1815) an issue with the same charter and category from
           the last 7 days. a notice only, nothing is blocked; Open uses a
           new tab so the draft survives, like the SOS link above -->
      <div v-if="dupNotice" class="od-add-dup" role="status">
        <span class="od-add-dup-text"><strong>Possible duplicate.</strong> {{ dupNotice.text }} You can still add this one.</span>
        <router-link :to="dupNotice.linkTo" target="_blank" class="btn btn-sm btn-outline-secondary od-add-dup-btn"
                     :aria-label="'Open event #' + dupNotice.id + ' in a new tab'">Open</router-link>
      </div>

      <!-- FI-2198 (#1820): who the trip was for, on every type. a first-class
           column rather than a schema field, so it stays off the public form;
           full width because the row above is a fixed three-column grid -->
      <div class="od-add-field">
        <label for="od-add-customer">Customer</label>
        <b-form-input id="od-add-customer" v-model="addEntry.customer" size="sm" maxlength="120"
                      placeholder="Group, company, or person"/>
      </div>

      <div class="od-add-field">
        <label for="od-add-location">Location</label>
        <b-form-input id="od-add-location" v-model="addEntry.location_text" size="sm" placeholder="Optional"/>
      </div>

      <!-- FI-1935: hand it off as you file it. defaults to you, which is what
           the server did silently before; Unassigned is an explicit choice. -->
      <div class="od-add-field">
        <label for="od-add-assigned">Assigned to</label>
        <b-form-select id="od-add-assigned" v-model="addEntry.assigned_user_id" :options="assignToOptions" size="sm"/>
      </div>

      <div class="od-add-field">
        <label>Photos</label>
        <od-photo-input
          ref="addEntryPhotoInput"
          :presign="presignPhoto"
          @input="addEntryPhotoKeys = $event"
          @busy="addEntryPhotosBusy = $event"
        />
      </div>

      <div v-if="addEntryError" class="od-add-error">{{ addEntryError }}</div>

      <div class="od-add-actions">
        <b-button v-if="addEntryDirty" variant="link" class="od-add-discard" :disabled="addEntrySubmitting" @click="resetAddEntryForm">Discard draft</b-button>
        <b-button variant="link" :disabled="addEntrySubmitting" @click="close">Cancel</b-button>
        <b-button variant="primary" :disabled="!canSubmitAddEntry || addEntrySubmitting || addEntryPhotosBusy" @click="submitAddEntry">
          <i class="fa fa-plus"></i>&nbsp;<span v-if="addEntrySubmitting">Saving...</span><span v-else-if="addEntryPhotosBusy">Uploading photos...</span><span v-else>Add entry</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
// FI-1815 "+ Add entry", lifted out of ops_desk.vue so the Event Log and the
// ERM Board offer the same form rather than two that drift apart. The whole
// lifecycle lives here — schema fetch and cache, the category-driven
// conditional fields, severity seeding, photo staging, and the discard
// guard. The host supplies the tenant, says whether SOS is on, and hears
// about a created event; it owns nothing else.
//
// FI-2215: a save closes the dialog and a toast names the event; clicking
// the toast asks the host to open it ('open-event'), the same path the old
// success step's Open event button took.
//
// Open it with `this.$refs.<ref>.open()`.
import { mapState } from 'vuex'
import moment from 'moment/moment'
import OdPhotoInput from './od_photo_input.vue'
import OdAddedToast from './od_added_toast.vue'
import {
  get_ops_desk_form_schema,
  create_ops_desk_event,
  get_ops_desk_possible_duplicates,
  presign_ops_desk_attachment,
  get_ops_desk_driver_roster,
  get_ops_desk_vehicle_roster,
  get_users,
  get_contact_users,
} from '../networking/apis'
import { assignableUsers, canReadDirectory } from '../utils/erm_assignees'
import { categoryLabel, customCategoryLabels } from '../utils/ops_labels'

const EMPTY_ENTRY = () => ({
  entry_type: 'incident',
  category: '',
  severity: 'normal',
  what_happened: '',
  follow_up_resolution: '',
  driver_id: '',
  vehicle_id: '',
  charter_ref: '',
  customer: '',
  location_text: '',
  // FI-1935: who works it. '' is an explicit "unassigned"; open() and the
  // reset default it to the signed-in user, matching the server default.
  assigned_user_id: '',
  extras: {},
})

// FI-2201 (#1815) the duplicate check waits for typing to settle
const DUP_DEBOUNCE_MS = 400

// FI-2215 how long the "event added" toast stays (hover or focus holds it)
const ADDED_TOAST_MS = 6000
// its own toaster, bottom center (od_added_toast.vue styles it);
// bootstrap-vue makes it on the first toast
const ADDED_TOASTER = 'od-added-toaster'

// FI-2215 hide every "event added" toast this dialog made that may still be
// up. a plain function so the specs' stand-in `this` needs no stub for it
function hideAddedToasts(vm) {
  const toaster = vm.$root && vm.$root.$bvToast
  if (toaster) (vm._addedToastIds || []).forEach(id => toaster.hide(id))
  vm._addedToastIds = []
}

// FI-2198 (#1820) schema fields the form already asks as a first-class field.
// customer_complaint's "Customer name (if known)" would repeat Customer, so
// this form drops it; the public form has no Customer field and keeps it.
const NATIVE_FIELD_KEYS = ['customer_name']

export default {
  name: 'od_add_entry_modal',
  components: { OdPhotoInput },
  props: {
    tenant: { type: String, default: '' },
    // FI-1820: surfaces the inline Bus SOS prompt on breakdown / accident.
    // the host owns the feature check, since it already reads tenant features.
    sosEnabled: { type: Boolean, default: false },
    // distinct per host so two mounted copies could never collide on the id
    modalId: { type: String, default: 'add-entry-modal' },
  },
  data() {
    return {
      // schemas are fetched on first open and cached so re-opens don't pay
      // another round-trip.
      addEntrySchemaLoaded: false,
      // FI-2116 canonical roster for the driver typeahead (fail-silent)
      rosterDrivers: [],
      rosterVehicles: [],
      // FI-1935 the tenant's people for the Assigned to picker (fail-silent)
      tenantUsers: [],
      tenantUsersLoaded: false,
      tenantUsersLoading: false,
      // FI-1935: Me is seeded once per draft; after that a blank means the
      // user chose Unassigned and a reopen must leave it alone
      assigneeSeeded: false,
      addEntryLoading: false,
      addEntrySubmitting: false,
      addEntryError: '',
      addEntrySchemas: { incident: {}, kudos: {}, note: {} },
      addEntryAvailableCategories: { incident: [], kudos: [], note: [] },
      // FI-1944 (#1816) slug -> the fleet's name, off the same schema (its
      // own categories and renamed built-ins)
      addEntryCategoryLabels: {},
      // FI-2173 the types the tenant offers today, from the schema payload's
      // entry_types (notes drop out while the Notes row is off). null until
      // the schema lands, which reads as all three
      addEntryEntryTypes: null,
      // populated from the schema fetch so the severity select can pre-fill
      // from the category default without re-deriving the map.
      severityDefaults: {},
      severityEditOptions: [
        { value: 'high', text: 'High' },
        { value: 'normal', text: 'Normal' },
        { value: 'low', text: 'Low' },
      ],
      addEntryTypeOptions: [
        { value: 'incident', label: 'Issue', icon: 'fa-exclamation-circle' },
        { value: 'kudos',    label: 'Kudos',    icon: 'fa-star' },
        { value: 'note',     label: 'Note',     icon: 'fa-sticky-note-o' },
      ],
      addEntry: EMPTY_ENTRY(),
      // FI-1812 photos: the staged keys ride the create payload.
      addEntryPhotoKeys: [],
      addEntryPhotosBusy: false,
      // FI-2201 (#1815) earlier issues on the draft's charter and category,
      // newest first, and the sequence that drops a superseded answer
      dupMatches: [],
      dupSeq: 0,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    currentUsername() {
      return (this.user && this.user.username) || ''
    },
    // FI-1935: unassigned, then you, then everyone else on the account as
    // "Full Name (username)". the event page's picker lists the same people.
    assignToOptions() {
      const me = this.currentUsername
      const opts = [{ value: '', text: 'Unassigned' }]
      const seen = new Set([''])
      if (me) {
        opts.push({ value: me, text: 'Me' })
        seen.add(me)
      }
      assignableUsers(this.tenantUsers).forEach(u => {
        const username = (u.username || '').trim()
        if (!username || seen.has(username)) return
        seen.add(username)
        const name = [u.first_name, u.last_name].map(p => (p || '').trim()).filter(Boolean).join(' ')
        opts.push({ value: username, text: name ? `${name} (${username})` : username })
      })
      return opts
    },
    showAddEntrySos() {
      return this.sosEnabled
        && this.addEntry.entry_type === 'incident'
        && (this.addEntry.category === 'breakdown' || this.addEntry.category === 'accident')
    },
    addEntrySosLinkTo() {
      const q = {}
      if (this.addEntry.vehicle_id) q.vehicle = this.addEntry.vehicle_id
      if (this.addEntry.location_text) q.notes = this.addEntry.location_text
      return { name: 'sos_list', query: q }
    },
    addEntryDirty() {
      const e = this.addEntry
      if (!e) return false
      if (e.category || (e.what_happened && e.what_happened.trim())) return true
      if (e.follow_up_resolution && e.follow_up_resolution.trim()) return true
      if (e.driver_id || e.vehicle_id || e.charter_ref || e.customer || e.location_text) return true
      const extras = e.extras || {}
      return Object.keys(extras).some(k => extras[k] !== '' && extras[k] !== null && extras[k] !== undefined && extras[k] !== false)
    },
    // the category field follows the schema's per-type list, not the type
    // literal: fleet_service.py hard-codes note: [] and submission_service
    // forces category 'note' on create, so a note never shows the field, and
    // this stays right if notes ever get real categories. caveat: an issue
    // type with zero enabled categories also hides the field and Add entry
    // stays disabled with no cue, which is exactly the public form's
    // behaviour (ops_desk_public_form.vue's v-if on categoriesForEntryType.length)
    showAddEntryCategory() {
      const t = this.addEntry.entry_type
      return ((this.addEntryAvailableCategories && this.addEntryAvailableCategories[t]) || []).length > 0
    },
    // the type row: the static list is the source of labels and icons, the
    // schema payload's entry_types says which of them the tenant offers.
    // a payload without the key (an older api) offers all three
    addEntryOfferedTypeOptions() {
      const offered = this.addEntryEntryTypes
      if (!Array.isArray(offered)) return this.addEntryTypeOptions
      return this.addEntryTypeOptions.filter(t => offered.includes(t.value))
    },
    addEntryCategoryOptions() {
      const t = this.addEntry.entry_type
      const cats = (this.addEntryAvailableCategories && this.addEntryAvailableCategories[t]) || []
      return [
        { value: '', text: 'Pick a category...', disabled: true },
        ...cats.map(c => ({ value: c, text: categoryLabel(c, this.addEntryCategoryLabels) })),
      ]
    },
    // per-(type, category) fields from the schema payload. the api
    // pre-merges entry-type-level fields into every category bucket, so
    // a category hit already carries them; _default covers the
    // no-category case (kudos before a pick, note always). mirrors the
    // public form's conditionalFields, less the NATIVE_FIELD_KEYS.
    addEntryRawConditionalFields() {
      const t = this.addEntry.entry_type
      const c = this.addEntry.category
      const bucket = (this.addEntrySchemas && this.addEntrySchemas[t]) || {}
      const fields = (c && bucket[c]) || bucket._default || []
      return fields.filter(f => !NATIVE_FIELD_KEYS.includes(f.key))
    },
    // show_if gates a follow-up field on a sibling's current value
    // (component "Other" -> "which component?"). hiding also DELETES the
    // typed value (watcher below): the server keeps non-empty gated values
    // for mobile clients, so the web must not submit a stale one.
    addEntryConditionalFields() {
      return this.addEntryRawConditionalFields.filter(
        f => !f.show_if || this.addEntry.extras[f.show_if.field] === f.show_if.value)
    },
    // FI-2152: a driver kudos asks for its recipient once, in the kudos block.
    // reactive through the same watcher $set that seeds kudos_target to ''.
    showKudosDriverField() {
      return this.addEntry.entry_type === 'kudos' && this.addEntry.extras.kudos_target === 'driver'
    },
    canSubmitAddEntry() {
      const t = this.addEntry.entry_type
      const needsCategory = t !== 'note'
      if (needsCategory && !this.addEntry.category) return false
      if (!this.addEntry.what_happened || !this.addEntry.what_happened.trim()) return false
      for (const f of this.addEntryConditionalFields) {
        if (!f.required) continue
        const v = this.addEntry.extras[f.key]
        if (v === undefined || v === null || v === '') return false
      }
      return true
    },
    // FI-2201 (#1815) what the duplicate check asks about: an issue with a
    // category and a charter. kudos and notes are never checked
    dupProbe() {
      const e = this.addEntry
      const charter = (e.charter_ref || '').trim()
      if (e.entry_type !== 'incident' || !e.category || !charter) return null
      return { entry_type: 'incident', category: e.category, charter_ref: charter }
    },
    dupProbeKey() {
      return this.dupProbe ? JSON.stringify(this.dupProbe) : ''
    },
    // the notice under the charter: the newest match by number, and how many
    // more there are. the category is the fleet's name for it
    dupNotice() {
      const matches = this.dupMatches || []
      const first = matches[0]
      const id = first && Number(first.id)
      if (!id) return null
      const label = categoryLabel(first.category || this.addEntry.category, this.addEntryCategoryLabels)
      const more = matches.length - 1
      const when = first.created_at ? ' on ' + moment(first.created_at).format('MMM D') : ' in the last 7 days'
      const text = more > 0
        ? '#' + id + ' and ' + more + ' more were filed as ' + label + ' on this charter in the last 7 days.'
        : '#' + id + ' was filed as ' + label + ' on this charter' + when + '.'
      return { id, text, linkTo: { name: 'ops_desk', query: { tenant: this.tenant, event_id: id } } }
    },
  },
  watch: {
    // seed select extras to '' so the placeholder option displays as
    // selected; an undefined v-model matches no option and the control
    // renders blank. also prune values of gated fields that just hid, so a
    // typed-then-rehidden value never rides the create payload.
    addEntryConditionalFields(fields) {
      (fields || []).forEach(f => {
        if (f.type === 'select' && this.addEntry.extras[f.key] === undefined) {
          this.$set(this.addEntry.extras, f.key, '')
        }
      })
      const visible = new Set((fields || []).map(f => f.key))
      this.addEntryRawConditionalFields.forEach(f => {
        if (f.show_if && !visible.has(f.key) && this.addEntry.extras[f.key] !== undefined) {
          this.$delete(this.addEntry.extras, f.key)
        }
      })
    },
    // FI-1933 re-seed the severity select to the category default whenever the
    // entry type or category changes; a manual override holds until the next
    // change. mirrors the extras-seeding pattern above.
    'addEntry.category'() { this.seedAddEntrySeverity() },
    'addEntry.entry_type'() { this.seedAddEntrySeverity() },
    // FI-2201 (#1815) any change to the type, category or charter drops the
    // notice at once (it named the old ones) and asks again once typing
    // settles; a draft with nothing to check just clears
    dupProbeKey(key) {
      if (this._dupTimer) {
        clearTimeout(this._dupTimer)
        this._dupTimer = null
      }
      this.dupSeq += 1
      this.dupMatches = []
      if (!key) return
      const probe = this.dupProbe
      this._dupTimer = setTimeout(() => {
        this._dupTimer = null
        this.checkDuplicates(probe)
      }, DUP_DEBOUNCE_MS)
    },
    // per-tenant schemas and categories: a tenant switch must drop the cache
    // and the half-typed draft, or the form would offer another fleet's
    // categories and file the entry against the wrong tenant.
    tenant() {
      this.addEntrySchemaLoaded = false
      this.addEntrySchemas = { incident: {}, kudos: {}, note: {} }
      this.addEntryAvailableCategories = { incident: [], kudos: [], note: [] }
      this.addEntryCategoryLabels = {}
      this.addEntryEntryTypes = null
      this.severityDefaults = {}
      // FI-1935: usernames are tenant-scoped, so the picker reloads too
      this.tenantUsers = []
      this.tenantUsersLoaded = false
      this.resetAddEntryForm()
      // FI-2215 a toast still up names the old fleet's event, and event
      // numbers repeat across fleets, so a click would open the wrong one
      hideAddedToasts(this)
    },
  },
  beforeDestroy() {
    if (this._dupTimer) clearTimeout(this._dupTimer)
    // FI-2215 an "event added" toast still up would open nothing once its
    // host is gone (the board routes away on open), so it goes with it
    hideAddedToasts(this)
  },
  methods: {
    // FI-1935 the Assigned to picker's people. same rule as the event page:
    // the directory when the role may read it, else the contacts list any
    // tenant user may read. a failure just leaves Unassigned and Me.
    async loadTenantUsers() {
      if (this.tenantUsersLoaded || this.tenantUsersLoading || !this.tenant || !this.user) return
      this.tenantUsersLoading = true
      try {
        const res = canReadDirectory(this.user)
          ? await get_users(this.tenant)
          : await get_contact_users(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        this.tenantUsers = Array.isArray(data.results) ? data.results : []
        this.tenantUsersLoaded = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('od_add_entry_modal loadTenantUsers failed', e)
      } finally {
        this.tenantUsersLoading = false
      }
    },

    async loadRoster() {
      if (!this.rosterDrivers.length) {
        try {
          const res = await get_ops_desk_driver_roster(this.tenant)
          this.rosterDrivers = ((res && res.value && res.value.data) || {}).drivers || []
        } catch (e) {
          // typeahead is a convenience only
          // eslint-disable-next-line no-console
          console.warn('od_add_entry_modal loadRoster failed', e)
        }
      }
      // FI-2098 vehicle half, independently fail-silent
      if (!this.rosterVehicles.length) {
        try {
          const res = await get_ops_desk_vehicle_roster(this.tenant)
          this.rosterVehicles = ((res && res.value && res.value.data) || {}).vehicles || []
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('od_add_entry_modal vehicle roster failed', e)
        }
      }
    },

    // the form is NOT reset on open: a half-filled draft survives close/reopen
    // and clears only after a successful submit or an explicit discard.
    async open() {
      if (!this.tenant) return
      // FI-2215 a toast still up would sit over this dialog's header, and a
      // click on it would open an event and drop the new draft unasked
      hideAddedToasts(this)
      this.addEntryError = ''
      this.$bvModal.show(this.modalId)
      this.loadRoster()
      this.loadTenantUsers()
      // a fresh draft starts on you, once; after that a blank is a choice
      if (!this.assigneeSeeded && !this.addEntry.assigned_user_id && this.currentUsername) {
        this.addEntry.assigned_user_id = this.currentUsername
        this.assigneeSeeded = true
      }
      // FI-2201 (#1815) a kept draft asks again: an issue may have been filed
      // on its charter, or the one named moved, since the last check
      if (this.dupProbeKey) this.checkDuplicates(this.dupProbe)
      if (this.addEntrySchemaLoaded) return
      this.addEntryLoading = true
      try {
        const res = await this.fetchFormSchema()
        const data = (res && res.value && res.value.data) || {}
        this.addEntryAvailableCategories = data.categories || { incident: [], kudos: [], note: [] }
        this.addEntryCategoryLabels = customCategoryLabels(data.custom_categories, data.label_overrides)
        // the payload nests the per-type buckets one level down, under
        // schemas.entry_types (see field_schemas.all_schemas)
        this.addEntrySchemas = (data.schemas && data.schemas.entry_types) || { incident: {}, kudos: {}, note: {} }
        this.severityDefaults = data.severity_defaults || {}
        // FI-2173 the offered types; a draft left on a type that is no
        // longer offered snaps to the first one so it cannot be submitted
        this.addEntryEntryTypes = Array.isArray(data.entry_types) ? data.entry_types.map(String) : null
        this.snapAddEntryTypeToOffered()
        this.snapAddEntryCategoryToOffered()
        this.addEntrySchemaLoaded = true
        // seed the severity select now the map is available (the modal opened
        // on incident with no category, so this lands 'normal').
        this.seedAddEntrySeverity()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('od_add_entry_modal schema load failed', e)
        this.addEntryError = 'Could not load the form. Try again in a moment.'
      } finally {
        this.addEntryLoading = false
      }
    },

    close() {
      this.$bvModal.hide(this.modalId)
    },

    // the schema fetch, a seam a spec can stand in for
    fetchFormSchema() {
      return get_ops_desk_form_schema(this.tenant)
    },
    // the create call, a seam too
    createEventRequest(payload) {
      return create_ops_desk_event(this.tenant, payload)
    },
    // FI-2201 (#1815) the duplicate check's fetch, a seam too
    fetchPossibleDuplicates(params, config) {
      return get_ops_desk_possible_duplicates(this.tenant, params, config)
    },
    // warn-only: an answer a newer probe superseded is dropped, a check the
    // fleet turned off shows nothing, and a failure is silent. a background
    // read, so a 403 rejects instead of logging the filer out mid-draft
    async checkDuplicates(probe) {
      if (!probe || !this.tenant) return
      const seq = ++this.dupSeq
      try {
        const res = await this.fetchPossibleDuplicates(probe, { noAuthRedirect: true })
        if (seq !== this.dupSeq) return
        const data = (res && res.value && res.value.data) || {}
        this.dupMatches = data.enabled && Array.isArray(data.matches) ? data.matches.slice() : []
      } catch (e) {
        if (seq !== this.dupSeq) return
        this.dupMatches = []
      }
    },
    // FI-1944 (#1816) the host calls this when its Settings closes: a
    // category added, renamed or turned off in there has to reach the next
    // open, so the cached schema goes and open() fetches it again. the draft
    // is kept, the same as a close and reopen, bar a category no longer
    // offered (snapAddEntryCategoryToOffered)
    invalidateSchema() {
      this.addEntrySchemaLoaded = false
      this.addEntryCategoryLabels = {}
    },

    // the category default for the severity select. kudos / notes are always
    // low; incidents read the server-provided map, defaulting to normal.
    defaultSeverityFor(entryType, category) {
      if (entryType === 'kudos' || entryType === 'note') return 'low'
      return (this.severityDefaults && this.severityDefaults[category]) || 'normal'
    },
    seedAddEntrySeverity() {
      this.addEntry.severity = this.defaultSeverityFor(this.addEntry.entry_type, this.addEntry.category)
    },

    // esc / header-x can eat a half-typed entry on one keystroke, so a
    // dirty form asks first; confirming discards it. the footer Cancel
    // closes without a prompt and keeps the draft for the next open.
    onAddEntryHide(evt) {
      const trigger = evt && evt.trigger
      if ((trigger === 'esc' || trigger === 'headerclose') && this.addEntryDirty) {
        // always stop this close, then decide once the key event has settled.
        // calling window.confirm() synchronously inside the Esc keydown lets
        // Safari's trailing Esc dismiss the native confirm the instant it
        // opens (it flashes and vanishes), so defer it to a macrotask that
        // runs after the keyup. the guard stops a repeated Esc from queuing
        // several confirms.
        evt.preventDefault()
        if (this._addEntryConfirming) return
        this._addEntryConfirming = true
        setTimeout(() => {
          this._addEntryConfirming = false
          if (window.confirm('Discard unsaved changes?')) {
            this.resetAddEntryForm()
            this.$bvModal.hide(this.modalId)
          }
        }, 0)
      }
    },

    onAddEntryHidden() {
      // flags only; the draft itself persists (see open()).
      this.addEntryError = ''
      this.addEntrySubmitting = false
    },

    // FI-2215 the save's confirmation: the dialog has closed, so the toast
    // is made on $root to outlive it. the text is the event number and the
    // fleet's name for the category, plus the charter when there is one
    showAddedToast(eventId, entry) {
      if (!eventId) return
      const toastId = this.modalId + '-added-' + eventId
      const tenant = this.tenant
      const content = this.$createElement(OdAddedToast, {
        props: {
          eventId,
          detail: this.addedToastDetail(entry),
          still: this.reducedMotion(),
          autoHideMs: ADDED_TOAST_MS,
        },
        on: {
          open: () => this.openAddedEvent(eventId, toastId, tenant),
          close: () => this.$root.$bvToast.hide(toastId),
        },
      })
      this._addedToastIds = (this._addedToastIds || []).concat(toastId)
      try {
        this.$root.$bvToast.toast(content, {
          id: toastId,
          toaster: ADDED_TOASTER,
          toastClass: 'od-added-toast',
          bodyClass: 'od-added-toast-body',
          // no header: the status region then reads "Event #N added" first
          noCloseButton: true,
          isStatus: true,
          solid: true,
          // the body owns the timer, so focus holds it as well as hover
          noAutoHide: true,
        })
      } catch (_) { /* toast plugin may not be ready on first paint */ }
    },
    addedToastDetail(entry) {
      const e = entry || {}
      const label = categoryLabel(e.category, this.addEntryCategoryLabels)
      const charter = (e.charter_ref || '').trim()
      if (label && charter) return label + ', charter ' + charter
      if (label) return label
      return charter ? 'Charter ' + charter : ''
    },
    reducedMotion() {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    // the host decides where an event opens: the Event Log stays put, the
    // board stamps ?from=board so the detail's back arrow returns there
    openAddedEvent(eventId, toastId, tenant) {
      this.$root.$bvToast.hide(toastId)
      // the hosts route with their current tenant: a toast from before a
      // fleet switch would open that fleet's same number, so it opens nothing
      if (tenant && tenant !== this.tenant) return
      this.$emit('open-event', eventId)
    },

    setAddEntryType(t) {
      if (this.addEntry.entry_type === t) return
      this.addEntry.entry_type = t
      this.addEntry.category = ''
      // wipe extras because schema membership changes by (type, category);
      // a stale value would survive into a different field's slot.
      this.addEntry.extras = {}
    },
    // the selected type must be one the row shows: with nothing offered
    // there is nowhere to snap, so the draft is left as it is
    snapAddEntryTypeToOffered() {
      const options = this.addEntryOfferedTypeOptions
      if (!options.length) return
      if (options.some(o => o.value === this.addEntry.entry_type)) return
      this.setAddEntryType(options[0].value)
    },
    // FI-1944 the same for the category once the schema is fetched again
    // (invalidateSchema): one turned off or removed in Settings since the
    // draft picked it would show blank and be refused on Add, so the pick
    // clears
    snapAddEntryCategoryToOffered() {
      const c = this.addEntry.category
      if (!c) return
      const cats = (this.addEntryAvailableCategories && this.addEntryAvailableCategories[this.addEntry.entry_type]) || []
      if (!cats.includes(c)) this.addEntry.category = ''
    },

    selectOptions(field) {
      const opts = Array.isArray(field.options) ? field.options : []
      return [
        { value: '', text: field.placeholder || 'Choose...', disabled: !!field.required },
        ...opts.map(o => ({ value: o.value, text: o.label || o.value })),
      ]
    },

    resetAddEntryForm() {
      this.addEntry = EMPTY_ENTRY()
      this.addEntry.assigned_user_id = this.currentUsername
      this.assigneeSeeded = !!this.currentUsername
      // FI-2201 (#1815) the notice was about the old draft
      this.dupMatches = []
      this.addEntryPhotoKeys = []
      if (this.$refs.addEntryPhotoInput) this.$refs.addEntryPhotoInput.clear()
      // a fresh draft must start on an offered type; the cached list still applies
      this.snapAddEntryTypeToOffered()
    },

    // FI-1812 photo staging: the browser POSTs straight to s3, dodging the
    // api-gateway payload cap, and the staged keys ride the create payload.
    async presignPhoto(file) {
      const res = await presign_ops_desk_attachment(this.tenant, {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
      })
      return (res && res.value && res.value.data) || null
    },

    async submitAddEntry() {
      if (!this.canSubmitAddEntry || this.addEntrySubmitting) return
      this.addEntryError = ''
      this.addEntrySubmitting = true
      try {
        const e = this.addEntry
        const payload = {
          entry_type: e.entry_type,
          category: e.category || (e.entry_type === 'note' ? 'note' : ''),
          what_happened: (e.what_happened || '').trim(),
          follow_up_resolution: (e.follow_up_resolution || '').trim() || undefined,
          driver_id: (e.driver_id || '').trim() || undefined,
          vehicle_id: (e.vehicle_id || '').trim() || undefined,
          charter_ref: (e.charter_ref || '').trim() || undefined,
          location_text: (e.location_text || '').trim() || undefined,
          // FI-1935: always explicit. '' is "leave it unassigned"; without the
          // key the server would assign the filer, which the picker now shows.
          assigned_user_id: e.assigned_user_id || '',
          // server splits schema-driven extras from first-class columns
          // (kudos_target, delay_duration_min); we just merge them in flat.
          ...e.extras,
          // after the spread so a schema field can never shadow these
          customer: (e.customer || '').trim() || undefined,
          severity: e.severity || undefined,
          attachment_keys: this.addEntryPhotoKeys.length ? this.addEntryPhotoKeys : undefined,
        }
        const res = await this.createEventRequest(payload)
        const data = (res && res.value && res.value.data) || {}
        // the draft is consumed once the entry lands
        this.resetAddEntryForm()
        // the host refreshes whatever it is showing, so the new entry appears
        // without a manual reload.
        this.$emit('created', data)
        // FI-2215 the dialog closes on save; the toast confirms and links
        this.close()
        this.showAddedToast(data.event_id, payload)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('od_add_entry_modal submit failed', err)
        // api errors are a flat {message} body; keep the error_data
        // path as a fallback for wrapped shapes.
        const body = err && err.response && err.response.data
        const msg = body && (body.message
          || (body.error_data && (body.error_data.error_message || body.error_data.message)))
        this.addEntryError = msg || 'Could not save the entry.'
      } finally {
        this.addEntrySubmitting = false
      }
    },
  },
}
</script>

<style scoped>
/* FI-1815 "+ Add entry" modal. compact single-column form with the entry
   type as a one-row chip track up top (FI-2173: label + connected pill of
   three equal segments, the log's filter-row voice), then category +
   conditional fields, then always-on context fields. tight spacing so the
   modal stays under-fold on a 13" laptop with the most common configurations. */
.od-add-loading { padding: 24px; text-align: center; color: #6b7280; font-size: 13.5px; }
.od-add-type-group { display: flex; align-items: center; gap: 6px; margin-bottom: 14px; }
/* the log's chip-group-label voice (ops_desk.vue .od-chip-group-label) */
.od-add-type-label {
  font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px;
  color: var(--fi-text-muted, #6b7280); white-space: nowrap;
}
/* grid, not inline-grid: as a flex item it hugs content because flex-basis
   auto resolves to max-content; 1fr auto-columns keep the segments equal */
.od-add-type-row {
  display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; min-width: 0; margin: 0;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  background: var(--fi-surface, #fff);
  overflow: hidden;
}
/* midnight.css keys on .od-add-type-btn / --active; keep the names */
.od-add-type-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-width: 0; white-space: nowrap; line-height: 1.4;
  background: transparent; border: 0; border-left: 1px solid var(--fi-line, #e8e8e6); border-radius: 0;
  padding: 5px 16px; font-size: 12.5px; font-weight: 600; color: var(--fi-text-muted, #6b7280);
  cursor: pointer; transition: background .15s, border-color .15s, color .15s;
}
.od-add-type-btn:first-child { border-left: 0; }
.od-add-type-btn:hover { background: var(--fi-surface-sunken, #f3f4f6); }
/* inset so the track's overflow:hidden cannot clip the ring */
.od-add-type-btn:focus-visible {
  outline: 2px solid var(--fi-ops-accent, #2e6da4);
  outline-offset: -2px;
}
/* the divider takes the fill too, else the active segment keeps a hairline seam */
.od-add-type-btn--active {
  background: var(--fi-ops-accent, #2e6da4); color: #fff;
  border-left-color: var(--fi-ops-accent, #2e6da4); cursor: default;
}
.od-add-type-btn--active:hover { background: var(--fi-ops-accent-strong, #1f4a73); border-left-color: var(--fi-ops-accent-strong, #1f4a73); }
/* the ring would vanish on its own accent fill; the active ink keeps it visible on every theme (midnight sets no outline) */
.od-add-type-btn--active:focus-visible { outline-color: currentColor; }

.od-add-field { margin-bottom: 12px; }
.od-add-field label {
  display: block; margin-bottom: 4px;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.od-add-help { font-size: 12px; color: #6b7280; margin: -2px 0 4px 0; }
.od-add-req { color: #b91c1c; font-weight: 700; }

/* tinted panel for the category-driven fields; mirrors the public
   form's .op-public-section--conditional treatment */
.od-add-conditional {
  background: #f8fafc; border: 1px solid #e1e8f0;
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 6px; padding: 12px 14px 2px 14px;
  margin-bottom: 12px;
}

.od-add-row {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px;
  margin-bottom: 12px;
}
.od-add-col label {
  display: block; margin-bottom: 4px;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}

.od-add-error {
  background: #fef2f2; border: 1px solid #fecaca;
  color: #b91c1c; border-radius: 4px;
  padding: 8px 10px; font-size: 13px; margin: 8px 0 12px 0;
}
.od-add-actions {
  display: flex; gap: 8px; justify-content: flex-end;
  margin-top: 14px; padding-top: 12px; border-top: 1px solid #eef0f3;
}
/* the draft-clearing link sits apart from the close/submit pair */
.od-add-discard { margin-right: auto; padding-left: 0; }

.od-add-sos {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  margin: 0 0 12px; padding: 9px 12px;
  border: 1px solid #f0d9a8; border-radius: 6px; background: #fdf6e7;
  font-size: 12.5px; color: #6b5518;
}
.od-add-sos-text strong { color: #8a6d1a; }
.od-add-sos-btn {
  white-space: nowrap; padding: 5px 10px; border-radius: 5px;
  background: #b7791f; color: #fff !important;
  font-size: 12px; font-weight: 600; text-decoration: none !important;
}
.od-add-sos-btn:hover { background: #9c6410; }

/* FI-2201 (#1815) the possible-duplicate notice: the sos box's amber advisory */
.od-add-dup {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  margin: 0 0 12px; padding: 9px 12px;
  border: 1px solid #f0d9a8; border-radius: 6px; background: #fdf6e7;
  font-size: 12.5px; color: #6b5518;
}
.od-add-dup-text strong { color: #8a6d1a; }
.od-add-dup-btn { white-space: nowrap; }

@media (max-width: 600px) {
  /* the track fills the row beside the label; taller segments and a bigger
     glyph for thumbs */
  .od-add-type-row { flex: 1 1 auto; }
  .od-add-type-btn { padding: 10px 6px; font-size: 13px; }
  .od-add-type-btn .fa { font-size: 15px; }
  .od-add-row { grid-template-columns: 1fr; }
  /* the notice's Open needs a thumb-sized box */
  .od-add-dup-btn { min-height: 44px; display: inline-flex; align-items: center; }
  /* fi-1935 widened to every control: ios safari zooms into any focused field under 16px */
  .od-add-form .form-control, .od-add-form .custom-select { font-size: 16px; }
}
</style>
